import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  type RefCallback,
  useRef,
  useState,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { autoUpdate, offset, useFloating } from '@floating-ui/react-dom';
import cx from 'classnames';

import { IconPlus16 } from 'src/assets/icons/16x16/IconPlus16';
import { IconArrow24 } from 'src/assets/icons/24x24/IconArrow24';
// @ts-expect-error "Modified" does not have types
import { IconModified } from 'src/assets/icons/Modified';
import { CustomIconButton } from 'src/components/CustomIconButton';
import { CustomInput } from 'src/components/CustomInput';
import { CustomMenu } from 'src/components/CustomMenu';
import { CustomTooltip } from 'src/components/CustomTooltip';
// @ts-expect-error "GenerationImproveList" does not have types
import { GenerationImproveList } from 'src/components/GenerationImproveList';
// @ts-expect-error "TruncatedTypography" does not have types
import { TruncatedTypography, TYPOGRAPHY_VARIANT } from 'src/components/TruncatedTypography';
import { GENERATION_IMPROVE_MODE, type ImproveParams } from 'src/data/generation';
import { STRUCTURE_INSTRUCTIONS_LABEL } from 'src/data/ui';
import { useBooleanState } from 'src/lib/hooks/useBooleanState';
import { type VoidFunction } from 'src/lib/lang';

import styles from './styles.module.css';

const DEFAULT_PLACEHOLDER = 'Make changes with prompt';

interface Props {
  className?: string;
  GenerationImproveListProps?: {
    isImageTool: boolean;
    options?: Array<(typeof GENERATION_IMPROVE_MODE)[keyof typeof GENERATION_IMPROVE_MODE]>;
    onSettingsClick?: () => void;
    ToneOfVoiceListProps?: Record<string, unknown>;
    ImageStyleListProps?: Record<string, unknown>;
    ImageOverlayProps?: Record<string, unknown>;
  };
  onSubmit: (improveParams: ImproveParams) => void;
  onMenuOpen?: VoidFunction;
  onMenuClose?: VoidFunction;
  onSettingsClick?: () => void;
  displayMenuButton?: boolean;
  displaySettingsButton?: boolean;
  disablePositioner?: boolean;
  seamlessMenu?: boolean;
  inlineMenu?: boolean;
  disabled?: boolean;
  placeholder?: string;
  alwaysShowInput?: boolean;
}

export const GenerationImproveControl: FC<Props> = ({
  className,
  GenerationImproveListProps,
  onMenuOpen,
  onMenuClose,
  onSubmit,
  onSettingsClick,
  displayMenuButton = true,
  displaySettingsButton = false,
  disablePositioner = false,
  seamlessMenu = false,
  inlineMenu = false,
  disabled = false,
  placeholder = DEFAULT_PLACEHOLDER,
  alwaysShowInput = false,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuPaperRef = useRef<HTMLDivElement | null>(null);
  const cursorStartOffsetRef = useRef<number>(0);
  const currentInputRef = useRef<HTMLTextAreaElement | null>(null);

  const [menuOpened, openMenu, closeMenu] = useBooleanState(inlineMenu);
  const [manualValue, setManualValue] = useState<string>('');
  const [inputFocused, setInputFocused] = useState<boolean>(false);

  const inputEmpty = manualValue.length === 0;
  const inputWidth = calculateInputWidth(rootRef.current, displaySettingsButton);

  const setInputRef: RefCallback<HTMLTextAreaElement> = (instance: HTMLTextAreaElement | null) => {
    if (!instance) {
      return;
    }

    if (currentInputRef.current === instance) {
      return;
    }

    currentInputRef.current = instance;
    if (cursorStartOffsetRef.current !== null) {
      // Skip setSelectionRange when offset is 0 and input is empty
      if (cursorStartOffsetRef.current === 0 && instance.value.length === 0) {
        cursorStartOffsetRef.current = 0;
        return;
      }

      instance.setSelectionRange(cursorStartOffsetRef.current, cursorStartOffsetRef.current);
      cursorStartOffsetRef.current = 0;
    }
  };

  const submitManualInstructions = () => {
    onSubmit({ improveMode: GENERATION_IMPROVE_MODE.instructions, instructions: manualValue });
    setManualValue('');
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setManualValue(event.target.value);
  };

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitManualInstructions();
    }
  };

  const handleInputBlur = () => {
    setInputFocused(false);
  };

  const handleSubmitClick = () => {
    submitManualInstructions();
  };

  const handleButtonMenuClick = () => {
    if (menuOpened) {
      closeMenu();
      onMenuClose?.();
    } else {
      openMenu();
      onMenuOpen?.();
    }
  };

  const handleMenuClose = (event: Event) => {
    event.stopPropagation();
    const target = event.target as Node;

    // Don't close if clicking inside our root element or the menu itself
    if (rootRef.current?.contains(target) || menuPaperRef.current?.contains(target)) {
      return;
    }

    closeMenu();
    onMenuClose?.();
  };

  const handleSubmitFromMenu = (improveParams: ImproveParams) => {
    // Use flushSync to ensure the menu closes immediately before calling onSubmit
    flushSync(() => {
      closeMenu();
    });
    onMenuClose?.();
    onSubmit(improveParams);
  };

  const handleUnfocusedValueClick = () => {
    cursorStartOffsetRef.current = 0;
    flushSync(() => {
      setInputFocused(true);
    });
  };

  const controls = (
    <div
      className={cx(styles.controls, {
        [styles.disabled]: disabled,
        [styles.inputFocused]: inputFocused,
        [styles.inputEmpty]: inputEmpty,
      })}
    >
      {displayMenuButton && (
        <CustomTooltip title="Quick prompts">
          <CustomIconButton
            data-testid="quickPromptsButton"
            className={styles.buttonMenu}
            onClick={handleButtonMenuClick}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled}
            variant="outlined"
            size="small"
          >
            <IconPlus16 />
          </CustomIconButton>
        </CustomTooltip>
      )}

      {inputFocused || alwaysShowInput ? (
        <CustomInput
          data-testid={'makeChangesWithPromptInput'}
          className={styles.input}
          style={{ width: inputWidth }}
          inputRef={setInputRef}
          placeholder={placeholder}
          value={manualValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={alwaysShowInput ? undefined : handleInputBlur}
          disabled={disabled}
          multiline
          fullWidth
          autoFocus={!alwaysShowInput}
        />
      ) : (
        <TruncatedTypography
          data-testid={'makeChangesWithPrompt'}
          className={styles.unfocusedValue}
          style={{ width: inputWidth }}
          variant={TYPOGRAPHY_VARIANT.regular14}
          onClick={handleUnfocusedValueClick}
        >
          {manualValue || placeholder}
        </TruncatedTypography>
      )}

      {displaySettingsButton && (
        <CustomTooltip title={STRUCTURE_INSTRUCTIONS_LABEL}>
          <CustomIconButton
            data-testid="structureFormatVocabularyButton"
            className={styles.buttonSettings}
            onClick={onSettingsClick}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled}
            variant="outlined"
            size="small"
          >
            <IconModified />
          </CustomIconButton>
        </CustomTooltip>
      )}

      <CustomTooltip title="Generate">
        <CustomIconButton
          data-testid="generateArrowButton"
          className={styles.buttonSubmit}
          onClick={handleSubmitClick}
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled}
          variant="outlined"
          size="small"
        >
          <IconArrow24 />
        </CustomIconButton>
      </CustomTooltip>
    </div>
  );

  const positionedControls =
    inputFocused && rootRef.current && !disablePositioner ? (
      <Positioner reference={rootRef.current}>{controls}</Positioner>
    ) : (
      controls
    );

  return (
    <>
      <div
        className={cx(styles.root, className, { [styles.root_inline]: inlineMenu })}
        ref={rootRef}
      >
        {positionedControls}

        {displayMenuButton && inlineMenu && menuOpened && (
          <GenerationImproveList
            className={cx(styles.generationImproveList, styles.generationImproveList_inline)}
            {...GenerationImproveListProps}
            displayHeader={false}
            onSubmit={handleSubmitFromMenu}
            disabled={disabled}
          />
        )}
      </div>

      {displayMenuButton && !inlineMenu && (
        <CustomMenu
          classes={{
            paper: cx(styles.menu__paper, { [styles.menu__paper_seamless]: seamlessMenu }),
          }}
          open={menuOpened}
          anchorEl={rootRef.current}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          getContentAnchorEl={null}
        >
          <GenerationImproveList
            className={cx(styles.generationImproveList, {
              [styles.generationImproveList_seamless]: seamlessMenu,
            })}
            style={{
              width: rootRef.current ? rootRef.current.getBoundingClientRect().width : undefined,
            }}
            {...GenerationImproveListProps}
            onSubmit={handleSubmitFromMenu}
            disabled={disabled}
          />
        </CustomMenu>
      )}
    </>
  );
};

interface PositionerProps {
  reference: Element;
  children: ReactNode;
}
const Positioner: FC<PositionerProps> = ({ reference, children }) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const { floatingStyles } = useFloating({
    elements: {
      floating: popperElement,
      reference,
    },
    placement: 'bottom',
    strategy: 'absolute',
    middleware: [offset({ mainAxis: -32 })],
    whileElementsMounted: autoUpdate,
  });

  return createPortal(
    <div
      className={styles.positioner}
      ref={setPopperElement}
      style={floatingStyles}
    >
      {children}
    </div>,
    document.body
  );
};

const calculateInputWidth = (
  rootElement: HTMLDivElement | null,
  displaySettingsButton: boolean
): number => {
  if (!rootElement) {
    return 200;
  }

  // sum of borders, paddings and buttons widths
  let occupiedSpace = 1 + 5 + 20 + 8 + 8 + 20 + 5 + 1;
  if (displaySettingsButton) {
    occupiedSpace += 24;
  }
  return rootElement.getBoundingClientRect().width - occupiedSpace;
};
